import { useEffect, useState, Fragment } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, X, ExternalLink, Copy, Truck, User, CreditCard, Package, Trash2, Palette } from "lucide-react";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null); // session_id
  const [detail, setDetail] = useState(null);     // current detail object
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data)); }, []);

  const statusColor = (s) =>
    s === "paid" ? "text-[#4CAF50]" : s === "pending" ? "text-[#B87333]" : s === "quote" ? "text-[#5FC7B8]" : "text-[#CF6679]";

  const paymentBadge = (o) => {
    if (o.order_type === "resin_lab") {
      return {
        label: "Simulator Quote",
        className: "text-[#5FC7B8] border border-[#5FC7B8]/50 bg-[#5FC7B8]/10 px-2 py-1 uppercase tracking-widest text-[10px]",
      };
    }
    return {
      label: o.payment_status || "—",
      className: `${statusColor(o.payment_status)} text-xs uppercase tracking-widest`,
    };
  };

  // Fulfillment status — colored badges per requirement (unfulfilled=yellow, shipped=blue, picked_up=green)
  const FULFILLMENT_OPTIONS = [
    { value: "unfulfilled", label: "Unfulfilled", color: "bg-[#B87333]/20 text-[#E5A55C] border-[#B87333]/60" },
    { value: "shipped",     label: "Shipped",     color: "bg-[#1F3A5F] text-[#7AB8E8] border-[#3B6AA0]" },
    { value: "picked_up",   label: "Picked Up",   color: "bg-[#1B3A1F] text-[#7BD08A] border-[#3F7A47]" },
  ];
  const fulfillmentBadge = (value) =>
    FULFILLMENT_OPTIONS.find((o) => o.value === value) || FULFILLMENT_OPTIONS[0];

  const updateFulfillment = async (sid, next) => {
    try {
      const { data } = await api.patch(`/admin/orders/${sid}/fulfillment`, { fulfillment_status: next });
      setOrders((arr) => arr.map((o) => (o.session_id === sid ? { ...o, ...data } : o)));
      if (detail?.session_id === sid) setDetail((d) => ({ ...d, ...data }));
      toast.success(`Marked ${fulfillmentBadge(next).label}`);
    } catch (e) {
      toast.error("Could not update fulfillment status");
    }
  };

  const deleteOrder = async (sid) => {
    if (!window.confirm("Are you sure you want to delete this order? This action cannot be undone.")) return;
    try {
      await api.delete(`/admin/orders/${sid}`);
      setOrders((arr) => arr.filter((o) => o.session_id !== sid));
      if (expanded === sid) { setExpanded(null); setDetail(null); }
      toast.success("Order deleted");
    } catch (e) {
      toast.error("Could not delete order");
    }
  };

  const toggle = async (sid) => {
    if (expanded === sid) { setExpanded(null); setDetail(null); return; }
    setExpanded(sid); setDetail(null); setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/orders/${sid}`);
      setDetail(data);
    } catch (e) {
      toast.error("Could not load order detail");
    } finally {
      setDetailLoading(false);
    }
  };

  const copy = (txt) => {
    if (!txt) return;
    navigator.clipboard.writeText(txt);
    toast.success("Copied");
  };

  const short = (id) => id ? `${id.slice(0, 10)}…${id.slice(-4)}` : "—";

  return (
    <div data-testid="admin-orders-page">
      <h1 className="font-display text-3xl sm:text-4xl text-[#F5F5DC] mb-8 hidden lg:block">Orders</h1>
      <p className="text-[#A3A3A3] text-sm mb-4 hidden md:block">Click any row to see the full shipping address, items with SKUs, and Stripe references.</p>

      {/* Desktop table */}
      <div className="hidden md:block bg-[#111] border border-[#2A2A2A] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-[#2A2A2A] bg-[#0A0A0A]">
            <tr>
              <th className="text-left p-4 smallcaps">When</th>
              <th className="text-left p-4 smallcaps">Customer</th>
              <th className="text-left p-4 smallcaps">Items</th>
              <th className="text-left p-4 smallcaps">Stripe ID</th>
              <th className="text-right p-4 smallcaps">Total</th>
              <th className="text-center p-4 smallcaps">Payment</th>
              <th className="text-center p-4 smallcaps">Fulfillment</th>
              <th className="p-4 w-10"></th>
              <th className="p-4 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => {
              const isOpen = expanded === o.session_id;
              return (
                <Fragment key={o.session_id}>
                  <tr
                    className={`border-b border-[#2A2A2A] cursor-pointer hover:bg-[#0A0A0A]/60 transition-colors ${isOpen ? "bg-[#0A0A0A]" : ""}`}
                    onClick={() => toggle(o.session_id)}
                    data-testid={`order-${o.session_id}`}
                  >
                    <td className="p-4 text-[#A3A3A3] text-xs align-top whitespace-nowrap">{new Date(o.created_at).toLocaleString()}</td>
                    <td className="p-4 align-top">
                      <div className="text-[#F5F5DC]" data-testid={`order-name-${o.session_id}`}>{o.customer_name || <span className="text-[#555]">—</span>}</div>
                      <div className="text-[#A3A3A3] text-xs">{o.customer_email || "—"}</div>
                    </td>
                    <td className="p-4 text-[#A3A3A3] align-top">
                      {o.order_type === "resin_lab" ? (
                        <div className="flex items-center gap-2" data-testid={`resin-lab-tag-${o.session_id}`}>
                          <Palette size={14} className="text-[#5FC7B8]" />
                          <span className="text-[#F5F5DC]">Custom Resin Lab design</span>
                        </div>
                      ) : (
                        <ul className="space-y-1">
                          {(o.items || []).map((i, idx) => (
                            <li key={idx} className="flex flex-wrap items-baseline gap-2" data-testid={`order-line-${o.session_id}-${idx}`}>
                              <span className="text-[#F5F5DC]">{i.qty}× {i.name}</span>
                              {i.sku && (
                                <span className="text-[10px] font-mono uppercase tracking-widest text-[#B87333] border border-[#B87333]/40 px-1.5 py-0.5" data-testid={`order-sku-${o.session_id}-${idx}`}>
                                  {i.sku}
                                </span>
                              )}
                              {i.on_sale && i.original_price > i.price && (
                                <span className="text-[10px] uppercase tracking-widest text-[#F5F5DC] bg-[#8B1A1A] px-1.5 py-0.5">Sale</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="p-4 align-top">
                      <span
                        className="font-mono text-[11px] text-[#A3A3A3] cursor-pointer hover:text-[#B87333]"
                        onClick={(e) => { e.stopPropagation(); copy(o.session_id); }}
                        title="Click to copy"
                        data-testid={`order-sid-${o.session_id}`}
                      >
                        {short(o.session_id)}
                      </span>
                    </td>
                    <td className="p-4 text-right text-[#F5F5DC] align-top whitespace-nowrap">{o.order_type === "resin_lab" ? <span className="text-[#A3A3A3] text-xs">Quote</span> : `$${o.amount?.toFixed(2)}`}</td>
                    <td className="p-4 text-center align-top">
                      {(() => {
                        const b = paymentBadge(o);
                        return <span className={b.className} data-testid={`payment-badge-${o.session_id}`}>{b.label}</span>;
                      })()}
                    </td>
                    <td className="p-4 text-center align-top" onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        const current = o.fulfillment_status || "unfulfilled";
                        const badge = fulfillmentBadge(current);
                        return (
                          <select
                            value={current}
                            onChange={(e) => updateFulfillment(o.session_id, e.target.value)}
                            data-testid={`fulfillment-${o.session_id}`}
                            className={`appearance-none cursor-pointer px-3 py-1.5 border text-[10px] uppercase tracking-widest font-medium ${badge.color} focus:outline-none focus:ring-1 focus:ring-[#B87333]`}
                          >
                            {FULFILLMENT_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value} className="bg-[#0A0A0A] text-[#F5F5DC] normal-case tracking-normal">
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        );
                      })()}
                    </td>
                    <td className="p-4 text-[#A3A3A3] align-top text-center">
                      <ChevronDown size={16} className={`transition-transform inline ${isOpen ? "rotate-180" : ""}`} />
                    </td>
                    <td className="p-4 align-top text-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => deleteOrder(o.session_id)}
                        className="text-red-500 hover:text-red-400 transition-colors p-1"
                        data-testid={`delete-order-${o.session_id}`}
                        aria-label="Delete order"
                        title="Delete order"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-[#2A2A2A]" data-testid={`order-detail-${o.session_id}`}>
                      <td colSpan={9} className="p-0 bg-[#0A0A0A]">
                        <OrderDetail loading={detailLoading} order={detail || o} onClose={() => toggle(o.session_id)} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {orders.length === 0 && <tr><td colSpan={9} className="p-12 text-center text-[#A3A3A3]">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {orders.map((o) => {
          const isOpen = expanded === o.session_id;
          return (
            <div key={o.session_id} className="bg-[#111] border border-[#2A2A2A]" data-testid={`order-m-${o.session_id}`}>
              <button
                type="button"
                className="w-full text-left p-4"
                onClick={() => toggle(o.session_id)}
              >
                <div className="flex justify-between items-start mb-2 gap-2">
                  <div className="min-w-0">
                    <p className="font-display text-lg text-[#F5F5DC] truncate" data-testid={`order-m-name-${o.session_id}`}>
                      {o.customer_name || o.customer_email || "—"}
                    </p>
                    <p className="text-xs text-[#A3A3A3] truncate">{o.customer_email || "—"}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {o.order_type === "resin_lab" ? (
                      <>
                        <p className="font-display text-sm text-[#A3A3A3]">Quote</p>
                        <span data-testid={`payment-badge-mobile-${o.session_id}`} className="text-[9px] uppercase tracking-widest text-[#5FC7B8] border border-[#5FC7B8]/50 bg-[#5FC7B8]/10 px-1.5 py-0.5">Simulator</span>
                      </>
                    ) : (
                      <>
                        <p className="font-display text-lg text-[#B87333]">${o.amount?.toFixed(2)}</p>
                        <span className={`text-[10px] uppercase tracking-widest ${statusColor(o.payment_status)}`}>{o.payment_status}</span>
                      </>
                    )}
                  </div>
                </div>
                {o.order_type === "resin_lab" ? (
                  <div className="text-xs text-[#F5F5DC] mt-1 flex items-center gap-2">
                    <Palette size={12} className="text-[#5FC7B8]" />
                    Custom Resin Lab design
                  </div>
                ) : (
                  <ul className="text-xs text-[#A3A3A3] mt-1 space-y-1">
                    {(o.items || []).map((i, idx) => (
                      <li key={idx} className="flex flex-wrap items-baseline gap-1.5">
                        <span className="text-[#F5F5DC]">{i.qty}× {i.name}</span>
                        {i.sku && <span className="text-[9px] font-mono uppercase tracking-widest text-[#B87333] border border-[#B87333]/40 px-1 py-0.5">{i.sku}</span>}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-[#555]">{new Date(o.created_at).toLocaleString()}</p>
                  <ChevronDown size={14} className={`text-[#A3A3A3] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
              </button>
              <div className="border-t border-[#2A2A2A] px-4 py-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Fulfillment:</span>
                {(() => {
                  const current = o.fulfillment_status || "unfulfilled";
                  const badge = fulfillmentBadge(current);
                  return (
                    <select
                      value={current}
                      onChange={(e) => updateFulfillment(o.session_id, e.target.value)}
                      data-testid={`fulfillment-m-${o.session_id}`}
                      className={`appearance-none cursor-pointer px-2 py-1 border text-[10px] uppercase tracking-widest font-medium ${badge.color} focus:outline-none flex-1`}
                    >
                      {FULFILLMENT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value} className="bg-[#0A0A0A] text-[#F5F5DC] normal-case tracking-normal">
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  );
                })()}
                <button
                  type="button"
                  onClick={() => deleteOrder(o.session_id)}
                  className="text-red-500 hover:text-red-400 p-1 flex-shrink-0"
                  data-testid={`delete-order-m-${o.session_id}`}
                  aria-label="Delete order"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              {isOpen && (
                <div className="border-t border-[#2A2A2A]">
                  <OrderDetail loading={detailLoading} order={detail || o} onClose={() => toggle(o.session_id)} mobile />
                </div>
              )}
            </div>
          );
        })}
        {orders.length === 0 && <p className="text-center text-[#A3A3A3] py-12">No orders yet.</p>}
      </div>
    </div>
  );
}

function DetailRow({ label, children, testid }) {
  return (
    <div className="flex justify-between gap-3 text-sm py-1" data-testid={testid}>
      <span className="text-[#A3A3A3]">{label}</span>
      <span className="text-[#F5F5DC] text-right">{children || "—"}</span>
    </div>
  );
}

function OrderDetail({ order, loading, onClose, mobile }) {
  if (loading) return <div className="p-6 text-[#A3A3A3] text-sm">Loading details…</div>;
  if (!order) return null;
  const isResinLab = order.order_type === "resin_lab";
  const ship = order.shipping_address_details || {};
  const items = order.items || [];
  const subtotal = items.reduce((s, i) => s + Number(i.price || 0) * Number(i.qty || 1), 0);
  const shipCost = Number(order.shipping_cost || 0);

  const copy = (txt) => {
    if (!txt) return;
    navigator.clipboard.writeText(txt);
    toast.success("Copied");
  };

  if (isResinLab) {
    return (
      <div className="p-6 grid gap-6 lg:grid-cols-[1fr_320px]" data-testid="order-detail-panel">
        {/* Design image */}
        <section>
          <h4 className="smallcaps text-[#5FC7B8] mb-3 flex items-center gap-2"><Palette size={12} /> Customer&apos;s Resin Lab design</h4>
          {order.design_image ? (
            <a
              href={order.design_image}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-[#111] border border-[#2A2A2A] hover:border-[#5FC7B8] transition-colors"
              data-testid="resin-lab-design-image-link"
              title="Open full size in a new tab"
            >
              <img
                src={order.design_image}
                alt="Customer resin lab design"
                className="w-full h-auto block"
                data-testid="resin-lab-design-image"
              />
            </a>
          ) : (
            <p className="text-[#A3A3A3] italic">Design image not available.</p>
          )}
          {(order.colors_used || []).length > 0 && (
            <div className="mt-4">
              <p className="smallcaps text-[#A3A3A3] mb-2">Colours used ({order.colors_used.length})</p>
              <div className="flex flex-wrap gap-2" data-testid="resin-lab-colors-used">
                {order.colors_used.map((name, idx) => (
                  <span key={idx} className="text-[11px] text-[#F5F5DC] border border-[#2A2A2A] px-2 py-1 uppercase tracking-widest">
                    {name}
                  </span>
                ))}
              </div>
            </div>
          )}
          {order.customer_note && (
            <div className="mt-4">
              <p className="smallcaps text-[#A3A3A3] mb-1">Customer note</p>
              <p className="text-sm text-[#F5F5DC] whitespace-pre-wrap">{order.customer_note}</p>
            </div>
          )}
        </section>
        {/* Customer contact */}
        <section>
          <h4 className="smallcaps text-[#B87333] mb-3 flex items-center gap-2"><User size={12} /> Customer</h4>
          <div className="space-y-1">
            <p className="font-display text-xl text-[#F5F5DC]" data-testid="detail-cust-name">{order.customer_name || "—"}</p>
            <p className="text-sm text-[#F5F5DC]" data-testid="detail-cust-email">{order.customer_email || "—"}</p>
            {order.customer_phone && <p className="text-sm text-[#F5F5DC]" data-testid="detail-cust-phone">{order.customer_phone}</p>}
          </div>
          <div className="mt-4 text-xs text-[#A3A3A3] leading-relaxed">
            This is a <span className="text-[#5FC7B8]">Simulator Quote</span> — the customer built this in the Resin Lab and is waiting for you to reply with a price + timeline.
          </div>
          <button
            type="button"
            className="w-full mt-4 p-2 bg-[#111] border border-[#2A2A2A] hover:border-[#B87333] flex justify-between items-center gap-2 text-[11px] font-mono"
            onClick={() => copy(order.session_id)}
            data-testid="detail-copy-session"
          >
            <span className="text-[#A3A3A3] truncate"><span className="text-[#555]">Order:</span> {order.session_id}</span>
            <Copy size={12} className="text-[#A3A3A3] flex-shrink-0" />
          </button>
        </section>
        {!mobile && (
          <button
            type="button"
            onClick={onClose}
            className="absolute top-2 right-2 text-[#A3A3A3] hover:text-[#F5F5DC] p-2"
            data-testid="detail-close"
          >
            <X size={16} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`p-6 grid gap-6 ${mobile ? "" : "lg:grid-cols-3"}`} data-testid="order-detail-panel">
      {/* Customer */}
      <section>
        <h4 className="smallcaps text-[#B87333] mb-3 flex items-center gap-2"><User size={12} /> Customer</h4>
        <div className="space-y-1">
          <p className="font-display text-xl text-[#F5F5DC]" data-testid="detail-cust-name">{order.customer_name || "—"}</p>
          <p className="text-sm text-[#F5F5DC]">{order.customer_email || "—"}</p>
          {order.customer_phone && <p className="text-sm text-[#A3A3A3]">{order.customer_phone}</p>}
        </div>
      </section>

      {/* Shipping */}
      <section>
        <h4 className="smallcaps text-[#B87333] mb-3 flex items-center gap-2"><Truck size={12} /> Shipping address</h4>
        <div className="text-sm text-[#F5F5DC] leading-relaxed" data-testid="detail-shipping-address">
          {ship.line1 ? (
            <>
              {ship.name && <p className="font-display text-lg">{ship.name}</p>}
              <p>{ship.line1}</p>
              {ship.line2 && <p>{ship.line2}</p>}
              <p>{[ship.city, ship.state, ship.postal_code].filter(Boolean).join(", ")}</p>
              <p>{ship.country}</p>
              {ship.phone && <p className="text-[#A3A3A3] mt-1">{ship.phone}</p>}
            </>
          ) : (
            <p className="text-[#A3A3A3] italic">
              {order.payment_status === "paid"
                ? "No shipping address captured (customer chose pickup or address sync pending)."
                : "Address will appear once payment is completed."}
            </p>
          )}
        </div>
        <p className="text-xs text-[#A3A3A3] mt-3"><span className="smallcaps">Method:</span> {order.shipping_label || order.shipping_method}</p>
      </section>

      {/* Items + Stripe refs */}
      <section className={mobile ? "" : "lg:col-span-1"}>
        <h4 className="smallcaps text-[#B87333] mb-3 flex items-center gap-2"><Package size={12} /> Items</h4>
        <div className="space-y-2 mb-4">
          {items.map((i, idx) => (
            <div key={idx} className="flex justify-between gap-3 text-sm border-b border-[#1F1F1F] pb-2" data-testid={`detail-item-${idx}`}>
              <div className="min-w-0">
                <p className="text-[#F5F5DC]">{i.name}</p>
                {i.sku && <p className="text-[10px] font-mono uppercase tracking-widest text-[#B87333]" data-testid={`detail-sku-${idx}`}>SKU: {i.sku}</p>}
              </div>
              <div className="text-right text-[#A3A3A3] whitespace-nowrap">
                <p>{i.qty}× ${Number(i.price).toFixed(2)}</p>
                {i.on_sale && i.original_price > i.price && (
                  <p className="text-[10px] line-through text-[#555]">${Number(i.original_price).toFixed(2)}</p>
                )}
              </div>
            </div>
          ))}
        </div>
        <DetailRow label="Subtotal">${subtotal.toFixed(2)}</DetailRow>
        <DetailRow label="Shipping">{shipCost > 0 ? `$${shipCost.toFixed(2)}` : "Free"}</DetailRow>
        <div className="flex justify-between gap-3 pt-2 border-t border-[#2A2A2A] mt-2">
          <span className="text-[#F5F5DC]">Total</span>
          <span className="font-display text-xl text-[#B87333]">${Number(order.amount || 0).toFixed(2)}</span>
        </div>

        <h4 className="smallcaps text-[#B87333] mt-6 mb-3 flex items-center gap-2"><CreditCard size={12} /> Stripe references</h4>
        <div className="space-y-2 text-[11px] font-mono">
          <button
            type="button"
            className="w-full text-left p-2 bg-[#111] border border-[#2A2A2A] hover:border-[#B87333] flex justify-between items-center gap-2 break-all"
            onClick={() => copy(order.session_id)}
            data-testid="detail-copy-session"
          >
            <span className="text-[#A3A3A3] truncate"><span className="text-[#555] not-italic">Session:</span> {order.session_id}</span>
            <Copy size={12} className="text-[#A3A3A3] flex-shrink-0" />
          </button>
          {order.payment_intent_id && (
            <button
              type="button"
              className="w-full text-left p-2 bg-[#111] border border-[#2A2A2A] hover:border-[#B87333] flex justify-between items-center gap-2 break-all"
              onClick={() => copy(order.payment_intent_id)}
              data-testid="detail-copy-pi"
            >
              <span className="text-[#A3A3A3] truncate"><span className="text-[#555]">Payment Intent:</span> {order.payment_intent_id}</span>
              <Copy size={12} className="text-[#A3A3A3] flex-shrink-0" />
            </button>
          )}
          {order.stripe_receipt_url && (
            <a
              href={order.stripe_receipt_url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full p-2 bg-[#111] border border-[#2A2A2A] hover:border-[#B87333] flex justify-between items-center gap-2 text-[#B87333]"
              data-testid="detail-receipt-link"
            >
              <span>View Stripe receipt</span>
              <ExternalLink size={12} />
            </a>
          )}
        </div>
      </section>

      {!mobile && (
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 text-[#A3A3A3] hover:text-[#F5F5DC] p-2"
          data-testid="detail-close"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}
